import React, { ReactElement, useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react'
import * as echarts from 'echarts/core'
import { TooltipComponent, ToolboxComponent } from 'echarts/components'
import { TreeChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import { Space, Card } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import type { IAppState } from '@/store/reducers'
import { useHistory, } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getTechTree, getTechTreeLeaf, getTechlId } from '@/services/rag'
import { autoClick, getUuid } from '@/utils/util'
import Nav from '@/components/nav'

echarts.use([TooltipComponent, ToolboxComponent, TreeChart, CanvasRenderer])

function TechTree(): ReactElement {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)

    const chartRef = useRef(null)
    const chartInstanceRef = useRef<any>(null)
    const mapObj = useRef<any>({})

    const [winH, setWinH] = useState(0)
    const [routes, ] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/techTree',
            breadcrumbName: t('techTree'),
        },
    ])

    // 迭代查出树中叶节点
    function queryTree(tree: any[], searchKey: string) {
        let stark: any[] = []
        stark = stark.concat(tree)
        while (stark.length) {
            const temp = stark.shift()
            if (temp?.children) {
                stark = stark.concat(temp.children)
            }
            if (temp.name === searchKey) {
                return temp
            }
        }
        return null
    }

    // 按字数换行
    function wrapText(text: string, maxLength: number, splitStr?: string): string {
        let result = ''
        let currentLine = ''
    
        for (let i = 0, len = text.length; i < len; i++) {
            // 添加当前字符到当前行
            currentLine += text[i]
    
            // 如果当前行长度达到了maxLength或者达到了字符串的末尾
            // 并且当前行不是空的（避免在字符串开头就添加换行符）
            if (currentLine.length === maxLength && i < len - 1) {
                // 将当前行添加到结果字符串，并加上换行符
                result += currentLine + (splitStr ?? '<br />')
                // 重置当前行为空，以便开始新的一行
                currentLine = ''
            } else if (i === len - 1 && currentLine.length > 0) {
                result += currentLine
            }
        }
    
        // 移除结果字符串末尾可能多余的换行符（如果原字符串长度正好是maxLength的倍数）
        if (result.endsWith('\n')) {
            result = result.slice(0, -1)
        }
    
        return result
    }

    const data: any = useRef({
        'name': 'ITMT',
        // 'itemStyle': {
        //     opacity: 0
        // },
        // 'lineStyle': {
        //     width: 0,
        //     color: 'red',
        //     curveness: 0
        // },
        // symbolSize: 0,
        // label: {
        //     show: false
        // },
        animationDuration: 550,
        animationDurationUpdate: 750,
        'children': []
    })
    const options = useRef({
        darkMode: true,
        tooltip: {
            trigger: 'item',
            triggerOn: 'mousemove'
        },
        series: [
            {
                type: 'tree',
                // edgeShape: 'polyline',
                roam: true,
                symbol(value: Array<any> | number, params: any) {
                    const { data } = params
                    if (data.itemStyle) {
                        return 'circle'
                    } else {
                        return 'emptyCircle'
                    }
                },
                symbolSize: 25,
                initialTreeDepth: 2,
                data: [data.current],
                top: '1%',
                left: '5%',
                bottom: '1%',
                right: '20%',
                label: {
                    position: 'left',
                    verticalAlign: 'middle',
                    align: 'right',
                    fontSize: 15,
                    formatter (params: any) {
                        return params.data.keyName
                    }
                },
                leaves: {
                    label: {
                        position: 'right',
                        verticalAlign: 'middle',
                        align: 'left'
                    }
                },
                lineStyle: {
                    width: 3
                },
                itemStyle: {
                    color: '#2589cc',
                },
                emphasis: {
                    focus: 'relative'
                },
                tooltip: {
                    // position(point: number[], params: any, dom: any, rect: any, size: any) {
                    //     console.log('111: ', point, params)
                    //     if (size.contentSize[1] > size.viewSize[1]) {
                    //         return 'top'
                    //     }
                    //     return [point[0] + 5, point[1] + 5]
                    // },
                    formatter(params: any) {
                        if (!params.data.leaf) {
                            return `<b>${wrapText(params.data.name_zh, 30)}</b><br/>
                                ${params.data.keywords_zh ? '&nbsp;&nbsp;&nbsp;&nbsp;' + wrapText(params.data.keywords_zh, 30) + '<br/>' : ''}
                                &nbsp;&nbsp;&nbsp;&nbsp;${wrapText(params.data.definition, 30)}<br/>
                                ${params.data.scope ? '&nbsp;&nbsp;&nbsp;&nbsp;' + wrapText(params.data.scope, 30) : ''}
                            `
                        } else {
                            return `<b>${wrapText(params.data.name_zh, 30)}</b><br/>
                                ${params.data.definition}
                            `
                        }
                    }
                },
                // expandAndCollapse: false,
            }
        ]
    })

    const fullOption = (element: any, chartInstance: any) => ({
        show: true,
        title: '全屏查看',
        right: 5,
        icon: 'path://M928 64H710.4c-14.933333 0-21.333333 17.066667-10.666667 27.733333l93.866667 93.866667-157.866667 157.866667c-12.8 12.8-12.8 32 0 44.8 6.4 6.4 14.933333 8.533333 23.466667 8.533333s17.066667-2.133333 23.466667-8.533333l157.866666-157.866667 93.866667 93.866667c10.666667 10.666667 27.733333 2.133333 27.733333-10.666667V96C960 78.933333 945.066667 64 928 64zM230.4 185.6l93.866667-93.866667c10.666667-10.666667 4.266667-27.733333-10.666667-27.733333H96C78.933333 64 64 78.933333 64 96v217.6c0 14.933333 17.066667 21.333333 27.733333 10.666667l93.866667-93.866667 157.866667 157.866667c6.4 6.4 14.933333 8.533333 23.466666 8.533333s17.066667-2.133333 23.466667-8.533333c12.8-12.8 12.8-32 0-44.8l-160-157.866667zM932.266667 699.733333l-93.866667 93.866667-157.866667-157.866667c-12.8-12.8-32-12.8-44.8 0s-12.8 32 0 44.8l157.866667 157.866667-93.866667 93.866667c-10.666667 10.666667-2.133333 27.733333 10.666667 27.733333h217.6c17.066667 0 32-14.933333 32-32V710.4c0-14.933333-17.066667-21.333333-27.733333-10.666667zM343.466667 635.733333l-157.866667 157.866667-93.866667-93.866667c-10.666667-10.666667-27.733333-4.266667-27.733333 10.666667v217.6c0 17.066667 14.933333 32 32 32h217.6c14.933333 0 21.333333-17.066667 10.666667-27.733333l-93.866667-93.866667 157.866667-157.866667c12.8-12.8 12.8-32 0-44.8s-32-12.8-44.8 0z',
        iconStyle: {
            color: '#4fb3ff',
            borderColor: '#4fb3ff'
        },
        onclick: () => {
            element.style.backgroundColor = 'rgb(41 29 114)'
            if (element.requestFullScreen) {
                element.requestFullScreen()
            } else if (element.msRequestFullscreen) {
                element.msRequestFullScreen()
            } else if (element.webkitRequestFullScreen) {
                element.webkitRequestFullScreen()
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen()
            }
            const oldOption = chartInstance.getOption()
            const newOption = {
                ...oldOption,
                series: options.current.series,
                toolbox: {
                    ...oldOption.toolbox[0],
                    feature: {
                        ...oldOption.toolbox[0].feature,
                        myFull: {
                            ...oldOption.toolbox[0].feature.myFull,
                            show: false
                        },
                        myExitFull: {
                            ...oldOption.toolbox[0].feature.myExitFull,
                            show: true
                        }
                    }
                }
            }
            chartInstance.setOption(newOption)
        },
    })
    const exitOption = (element: any, chartInstance: any) => ({
        show: false,
        title: '退出全屏',
        icon: 'path://M320 128h64v256H128v-64h146.88l-169.6-169.28 45.44-45.44L320 274.88z m416 146.88V128h-64v256h256v-64h-146.88l169.6-169.28-45.44-45.44zM928 704v-64h-256v256h64v-146.88l169.28 169.6 45.44-45.44-169.6-169.28zM128 704h146.88l-169.6 169.28 45.44 45.44 169.28-169.6V896h64v-256H128z',
        iconStyle: {
            color: '#4fb3ff',
            borderColor: '#4fb3ff'
        },
        onclick: () => {
            console.log('document.fullscreenElement: ', document.fullscreenElement)
            element.style.background = ''
            // 退出全屏
            if (document.fullscreenElement) {
                document.exitFullscreen()
            }
            const option = chartInstance.getOption()
            const newOption = {
                ...option,
                toolbox: {
                    ...option.toolbox[0],
                    feature: {
                        ...option.toolbox[0].feature,
                        myFull: {
                            ...option.toolbox[0].feature.myFull,
                            show: true
                        },
                        myExitFull: {
                            ...option.toolbox[0].feature.myExitFull,
                            show: false
                        }
                    }
                }
            }
            chartInstance.setOption(newOption)
            setTimeout(() => {
                chartInstance.resize()
                const childs = element.childNodes
                childs[childs.length - 1].style.display = 'none'
            }, 100)
        }
    })

    // 第一层
    const getTechTreeInit = async () => {
        const resData = await getTechTree()
        data.current.children = resData.map((item: any) => {
            const obj = {
                ...item,
                name: item.id,
                definition: item.definition.replace(/\n/g, '<br/>'),
                collapsed: false,
                level: 1,
            }
            if (!mapObj.current[item.id]) {
                mapObj.current[item.id] = { ...obj, parent: data.current }
            }
            return obj
        })
        chartInstanceRef.current.setOption({
            ...options.current,
            toolbox: {
                feature: {
                    // 全屏具体实现
                    myFull: fullOption(chartRef.current, chartInstanceRef.current),
                    myExitFull: exitOption(chartRef.current, chartInstanceRef.current)
                },
            }
        })
    }
    // 二、三层
    const getTechTreeChild = async ({ id, level }: {
        id: number | string
        scope: string
        level: number
    }, node: any) => {
        const myChart = chartInstanceRef.current
        myChart.showLoading()
        const resData = await getTechTree({ id }).finally(() => {
            myChart.hideLoading()
        })
        node.children = resData.map((item: any) => {
            const obj = {
                ...item,
                name: item.id,
                collapsed: true,
                level: level + 1,
            }
            if (!mapObj.current[item.id]) {
                mapObj.current[item.id] = { ...obj, parent: node }
            }
            return obj
        })
        node.collapsed = false
        myChart.setOption({
            ...options.current,
            series: [{
                ...options.current.series[0],
                data: [data.current]
            }],
            toolbox: {
                feature: {
                    // 全屏具体实现
                    myFull: fullOption(chartRef.current, chartInstanceRef.current),
                    myExitFull: exitOption(chartRef.current, chartInstanceRef.current)
                },
            }
        }, true)
    }
    // 最后一层叶子节点，获取接口返回信息
    const getTechTreeLeafFn = async (node: any) => {
        const myChart = chartInstanceRef.current
        myChart.showLoading()
        const resData = await getTechTreeLeaf(node.id).finally(() => {
            myChart.hideLoading()
        })
        console.log('resData: ', resData)
        const keys = Object.keys(resData)
        if (keys.length === 0) {
            console.log('node: ', node, data.current, mapObj.current[node.id])
            node.itemStyle = {
                color: 'red'
            }
            const siblings = mapObj.current[node.id].parent.children
            const filterArr = siblings.filter((item: any) => !!item.itemStyle)
            if (filterArr.length > 1) {
                const parentNode = queryTree([data.current], node.parent.id)
                parentNode.itemStyle = {
                    color: 'yellow'
                }
            }
        } else {
            node.children = keys.map((key: string) => {
                const definitionBr = resData[key].replace(/\n/g, '<br/>')
                const arr = definitionBr.split('<br/>')
                const result = arr.map((item: string) => wrapText(item, 60))
                const obj: any = {
                    name: getUuid(8),
                    keyName: key.length > 20 ? key.substring(0, 20) + '...' : key,
                    name_en: key,
                    name_zh: key,
                    definition: result.join('<br/>'),
                    scope: '',
                    keywords_en: '',
                    keywords_zh: '',
                    collapsed: false,
                    leaf: true,
                }
                if (!mapObj.current[key]) {
                    mapObj.current[key] = { ...obj, parent: node }
                }
                return obj
            })
            node.collapsed = false
        }
        myChart.setOption({
            ...options.current,
            series: [{
                ...options.current.series[0],
                data: [data.current]
            }],
            toolbox: {
                feature: {
                    // 全屏具体实现
                    myFull: fullOption(chartRef.current, chartInstanceRef.current),
                    myExitFull: exitOption(chartRef.current, chartInstanceRef.current)
                },
            }
        }, true)
    }
    // 跳转到技术详情
    const gotoDetail = async ({ keyName }: {
        keyName: string
    }) => {
        const myChart = chartInstanceRef.current
        myChart.showLoading()
        const resData = await getTechlId({
            ptns: [keyName]
        }).finally(() => {
            myChart.hideLoading()
        })
        autoClick(`/projectInfoView?workGroupId=1&projectId=${resData[keyName]}&isView=true`)
    }

    const handlerClick = useCallback((param: any) => {
        console.log('param: ', param)

        if (typeof param.seriesIndex == 'undefined') {
            return
        }
        if (param.type == 'click') {
            const node = queryTree([data.current], param.data.name)
            if (node.collapsed !== undefined) {
                node.collapsed = !node.collapsed
            } else if (!param.data.itemStyle) {
                node.collapsed = false
            }
            if (!param.data.children && !param.data.leaf) {
                if (param.data.level < 3) {
                    getTechTreeChild(param.data, node)
                } else if (!param.data.itemStyle) {
                    getTechTreeLeafFn(node)
                }
            } else if (param.data.leaf) {
                gotoDetail(node)
            }
        }
    }, [])

    useLayoutEffect(() => {
        if (chartRef.current) {
            const chartInstance = echarts.init(chartRef.current)
            chartInstanceRef.current = chartInstance
            getTechTreeInit()
            
            chartInstance.on('click', handlerClick)
            return () => {
                chartInstance.off('click', handlerClick)
            }
        }
    }, [workgroupList])

    useEffect(() => {
        const offsetH = 200
        setWinH(document.body.clientHeight - offsetH)
        const onResize = () => {
            setWinH(document.body.clientHeight - offsetH)
        }
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [])
    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.resize()
        }
    }, [winH])

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            <Card className="edit_table body_pad0" style={{ paddingTop: 0, background: 'rgb(41 29 114)' }}>
                <div ref={chartRef} style={{ width: '100%', height: winH }}></div>
            </Card>
        </Space>
    )
}

export default TechTree